import {
  EXAM_TYPES,
  type ExamType,
  toExamTypes,
  toGqlExamType,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  styled,
  Typography,
} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { EXAM_LABELS } from "../../_lib/epocus-exam-constants";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { selectEnabledExamTypes } from "../../_lib/use-enabled-exam-types";
import { linkToEpocusSettings } from "../../links";
import { SettingsCard, SettingsCardLabel } from "../_lib/settings-card";
import { useCanManageProgramUsers } from "../_lib/use-can-manage-program-users";
import {
  useEpocusProgramConfigQuery,
  useEpocusSetProgramEnabledExamTypesMutation,
} from "./_lib/__generated__/program-config-operations.gql";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const ExamTypeList = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const ExamTypeRow = styled(FormControlLabel)({
  margin: 0,
  minHeight: 44,
});

const ActionRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
});

export const EpocusProgramConfigPage: FC = () => {
  const canManage = useCanManageProgramUsers();
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;
  const { data, error: configError } = useEpocusProgramConfigQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });
  const [setEnabledExamTypes, { loading: saving }] =
    useEpocusSetProgramEnabledExamTypesMutation();

  const [selected, setSelected] = useState<readonly ExamType[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const editedProgramId = useRef<string | null>(null);

  useEffect(() => {
    if (!programId || !data || editedProgramId.current === programId) return;
    setSelected(
      selectEnabledExamTypes(
        toExamTypes(data.programConfig?.enabledExamTypes ?? []),
      ),
    );
  }, [programId, data]);

  if (!canManage) {
    return <Navigate to={linkToEpocusSettings()} replace />;
  }

  const toggle = (examType: ExamType) => {
    editedProgramId.current = programId;
    setSaved(false);
    setSelected((current) =>
      current?.includes(examType)
        ? current.filter((type) => type !== examType)
        : [...(current ?? []), examType],
    );
  };

  const save = async () => {
    if (!programId || !selected) return;
    setError(null);
    setSaved(false);
    try {
      await setEnabledExamTypes({
        variables: {
          input: {
            programId,
            enabledExamTypes:
              selected.length === EXAM_TYPES.length
                ? []
                : selected.map(toGqlExamType),
          },
        },
      });
      editedProgramId.current = null;
      setSaved(true);
    } catch {
      setError("Couldn't save. Please try again.");
    }
  };

  return (
    <PageRoot>
      <Box>
        <Typography
          variant="h5"
          style={{
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            fontSize: rem(24),
            lineHeight: 1.2,
          }}
        >
          Program configuration
        </Typography>
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginTop: 4,
          }}
        >
          How ePOCUS behaves for everyone in {program?.name ?? "your program"}
        </Typography>
      </Box>

      {!selected && !configError && (programsLoading || programId !== null) ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading your program" />
        </LoadingRow>
      ) : !selected && configError ? (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            Couldn't load your program configuration.
          </Typography>
        </SettingsCard>
      ) : !selected ? (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            Couldn't determine your program.
          </Typography>
        </SettingsCard>
      ) : (
        <SettingsCard>
          <SettingsCardLabel>Exam types</SettingsCardLabel>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            Only the exam types you enable appear when someone starts or
            documents a scan. Exams already recorded against a type you turn off
            stay viewable and reviewable.
          </Typography>

          <ExamTypeList>
            {EXAM_TYPES.map((examType) => (
              <ExamTypeRow
                key={examType}
                control={
                  <Checkbox
                    color="primary"
                    checked={selected.includes(examType)}
                    disabled={saving}
                    onChange={() => toggle(examType)}
                  />
                }
                label={
                  <Typography
                    style={{ fontSize: rem(15), color: EPOCUS_TEXT_PRIMARY }}
                  >
                    {EXAM_LABELS[examType]}
                  </Typography>
                }
              />
            ))}
          </ExamTypeList>

          <ActionRow>
            <Button
              variant="contained"
              color="primary"
              disabled={saving || selected.length === 0}
              onClick={save}
            >
              Save
            </Button>
            {selected.length === 0 && (
              <Typography
                style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
              >
                Enable at least one exam type. Saving none would turn every exam
                type back on.
              </Typography>
            )}
            {saved && selected.length > 0 && (
              <Typography
                style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
              >
                Saved. Everyone's app picks this up on its next sync.
              </Typography>
            )}
          </ActionRow>

          {error && (
            <Typography style={{ fontSize: rem(13), color: "#dc2626" }}>
              {error}
            </Typography>
          )}
        </SettingsCard>
      )}
    </PageRoot>
  );
};
